/**
 * BUD-18: naming a root with a nostr event.
 *
 * An `nhash` pins one exact root forever. A kind 30064 parameterized replaceable
 * event does the opposite — it gives a tree a *name* under an npub, so
 * `htree://<npub>/<tree>/<path>` keeps resolving as the owner publishes new
 * roots. That is what makes hashtrees usable for websites, backups and app data
 * rather than only for snapshots.
 *
 * The interesting part is how the root key travels, since our whole tree is
 * encrypted and one key opens it:
 *
 *   public        `key` tag, plain hex — anyone who reads the event can open the tree
 *   link-private  `encryptedKey` = root_key XOR link_key, with the link_key only in the URL
 *   owner-private `selfEncryptedKey`, NIP-44 encrypted to the author — only they can open it
 */

import { finalizeEvent, getPublicKey, type EventTemplate, type VerifiedEvent } from "nostr-tools/pure";
import { npubEncode } from "nostr-tools/nip19";
import * as nip44 from "nostr-tools/nip44";
import { fromHex, sha256, toHex, xorBytes } from "./bytes";

/** BUD-18 hashtree root event kind. */
export const ROOT_KIND = 30064;
/** Older implementations published roots as app-data events. Readers MAY still accept these. */
export const LEGACY_ROOT_KIND = 30078;

export const DEFAULT_RELAYS = ["wss://relay.damus.io", "wss://nos.lol", "wss://relay.primal.net"];

export type Visibility = "public" | "link-private" | "owner-private";

export interface RootEventOptions {
  treeName: string;
  rootHash: Uint8Array;
  /** The CHK key of the root manifest. Every tree this app builds is encrypted, so this is always set. */
  rootKey: Uint8Array;
  visibility: Visibility;
  /** 32 random bytes, required for `link-private`. Shared only via the URL. */
  linkKey?: Uint8Array;
}

export interface BuiltRootEvent {
  template: EventTemplate;
  /** Extra material the user needs to keep, e.g. the link key for a link-private root. */
  linkKey?: Uint8Array;
}

export function generateLinkKey(): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(32));
}

/** `keyId` = first 8 bytes of SHA256(link_key), so a client can tell whether its key fits. */
export async function linkKeyId(linkKey: Uint8Array): Promise<string> {
  return toHex((await sha256(linkKey)).slice(0, 8));
}

/**
 * Build the kind 30064 event describing a root.
 *
 * The `d` tag is the tree name, which is what makes the event replaceable per
 * tree: publishing again with the same `d` supersedes the old root.
 */
export async function buildRootEvent(
  options: RootEventOptions,
  secretKey: Uint8Array,
): Promise<BuiltRootEvent> {
  const { treeName, rootHash, rootKey, visibility } = options;
  if (rootHash.length !== 32) throw new Error("root hash must be 32 bytes");
  if (rootKey.length !== 32) throw new Error("root key must be 32 bytes");
  if (!treeName.trim()) throw new Error("tree name must not be empty");

  const tags: string[][] = [
    ["d", treeName],
    ["hash", toHex(rootHash)],
    ["l", "hashtree"],
  ];

  let linkKey: Uint8Array | undefined;

  if (visibility === "public") {
    // Anyone who can read the event can open the tree.
    tags.push(["key", toHex(rootKey)]);
  } else if (visibility === "link-private") {
    linkKey = options.linkKey ?? generateLinkKey();
    tags.push(["encryptedKey", toHex(xorBytes(rootKey, linkKey))]);
    tags.push(["keyId", await linkKeyId(linkKey)]);
    // So the owner can still open their own tree without the shared link.
    tags.push(["selfEncryptedKey", selfEncrypt(toHex(rootKey), secretKey)]);
  } else {
    tags.push(["selfEncryptedKey", selfEncrypt(toHex(rootKey), secretKey)]);
  }

  return {
    template: {
      kind: ROOT_KIND,
      created_at: Math.floor(Date.now() / 1000),
      content: "",
      tags,
    },
    ...(linkKey ? { linkKey } : {}),
  };
}

/** NIP-44 encrypt to yourself: the conversation key of a pubkey with its own secret. */
function selfEncrypt(plaintext: string, secretKey: Uint8Array): string {
  const conversationKey = nip44.getConversationKey(secretKey, getPublicKey(secretKey));
  return nip44.encrypt(plaintext, conversationKey);
}

/** Recover an owner-private root key. Used by the verification pass. */
export function selfDecrypt(ciphertext: string, secretKey: Uint8Array): string {
  const conversationKey = nip44.getConversationKey(secretKey, getPublicKey(secretKey));
  return nip44.decrypt(ciphertext, conversationKey);
}

export function signRootEvent(template: EventTemplate, secretKey: Uint8Array): VerifiedEvent {
  return finalizeEvent(template, secretKey);
}

/**
 * Recover the root key from a published event, exactly as a reading client would.
 *
 * `linkKeyHex` comes from the `k=` query parameter of a link-private reference.
 */
export async function recoverRootKey(
  event: { tags: string[][] },
  options: { linkKeyHex?: string; secretKey?: Uint8Array } = {},
): Promise<Uint8Array> {
  const tag = (name: string) => event.tags.find(([key]) => key === name)?.[1];

  const publicKey = tag("key");
  if (publicKey) return fromHex(publicKey);

  const encryptedKey = tag("encryptedKey");
  if (encryptedKey && options.linkKeyHex) {
    const linkKey = fromHex(options.linkKeyHex);
    const expectedId = tag("keyId");
    if (expectedId) {
      const actualId = await linkKeyId(linkKey);
      if (actualId !== expectedId) throw new Error(`link key does not match keyId ${expectedId}`);
    }
    return xorBytes(fromHex(encryptedKey), linkKey);
  }

  const selfEncryptedKey = tag("selfEncryptedKey");
  if (selfEncryptedKey && options.secretKey) return fromHex(selfDecrypt(selfEncryptedKey, options.secretKey));

  throw new Error("no root key available: this root is private and no usable key was supplied");
}

export interface PublishOutcome {
  relay: string;
  ok: boolean;
  message?: string;
}

/**
 * Publish to several relays over raw websockets.
 *
 * nostr-tools' pool works too, but a hand-written `["EVENT", ...]` send makes
 * the wire format visible, which suits an explainer.
 */
export async function publishToRelay(relay: string, event: VerifiedEvent, timeoutMs = 8000): Promise<PublishOutcome> {
  return new Promise<PublishOutcome>((resolve) => {
    let settled = false;
    const finish = (outcome: PublishOutcome) => {
      if (settled) return;
      settled = true;
      try {
        socket.close();
      } catch {
        // already closing
      }
      resolve(outcome);
    };

    let socket: WebSocket;
    try {
      socket = new WebSocket(relay);
    } catch (error) {
      resolve({ relay, ok: false, message: error instanceof Error ? error.message : String(error) });
      return;
    }

    const timer = setTimeout(() => finish({ relay, ok: false, message: "timed out" }), timeoutMs);

    socket.onopen = () => socket.send(JSON.stringify(["EVENT", event]));

    socket.onmessage = (message) => {
      try {
        const data = JSON.parse(String(message.data));
        if (data[0] === "OK" && data[1] === event.id) {
          clearTimeout(timer);
          finish({ relay, ok: Boolean(data[2]), message: data[3] || undefined });
        }
      } catch {
        // ignore anything that is not a well formed relay message
      }
    };

    socket.onerror = () => {
      clearTimeout(timer);
      finish({ relay, ok: false, message: "connection failed" });
    };

    socket.onclose = () => {
      clearTimeout(timer);
      finish({ relay, ok: false, message: "closed before acknowledging" });
    };
  });
}

export async function publishToRelays(relays: string[], event: VerifiedEvent): Promise<PublishOutcome[]> {
  return Promise.all(relays.map((relay) => publishToRelay(relay, event)));
}

export { npubEncode };
